import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

// Types for payroll deductions
export interface PayrollDeduction {
  uuid: string;
  name: string;
  abn: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  head_office_location: string;
  created_at: string;
  updated_at: string;
  donation_request_status: string;
  status_uuid: string;
  status_name: string;
  total_deduction: number;
}

export interface PayrollDeductionFilters {
  page?: number;
  per_page?: number;
  phrase?: string;
  employer_uuid?: string;
  start_date?: string;
  end_date?: string;
}

export interface PayrollDeductionsResponse {
  success: boolean;
  message?: string;
  data: PayrollDeduction[];
  paging: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
  errors?: string[];
}

class PayrollDeductionsService {
  private baseUrl = '/admin/payroll-deductions';

  /**
   * Get paginated list of payroll deductions
   */
  async getPayrollDeductions(filters: PayrollDeductionFilters = {}): Promise<PayrollDeductionsResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      if (filters.phrase) params.append('phrase', filters.phrase);
      if (filters.employer_uuid) params.append('employer_uuid', filters.employer_uuid);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get<PayrollDeductionsResponse>(`${this.baseUrl}?${params.toString()}`);
      
      const items = extractListItems<PayrollDeduction>(response.data.data);
      const paging = normalizePaging(
        extractResponsePaging(response) ?? extractPaging(response.data.data),
        items.length
      );
      return {
        success: true,
        data: items,
        paging: paging as PayrollDeductionsResponse['paging'],
        message: 'Payroll deductions retrieved successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        paging: {
          current_page: 0,
          last_page: 0,
          per_page: 15,
          total: 0,
          from: 0,
          to: 0
        },
        message: error.response?.data?.message || 'Failed to retrieve payroll deductions',
        errors: [error.message]
      };
    }
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Get status display name
   */
  static getStatusDisplayName(status: string): string {
    const statusMap: Record<string, string> = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed'
    };
    return statusMap[status.toLowerCase()] || status;
  }
}

export default PayrollDeductionsService; 